/**
 * GeneratedImageDisplay Component
 *
 * Displays generated AI image with metadata and actions.
 * Shows loading states, aspect ratio, dimensions, and file size.
 *
 * Usage:
 * <GeneratedImageDisplay
 *   imageUrl="https://..."
 *   aspectRatio="4:5"
 *   width={1080}
 *   height={1350}
 *   fileSize={185000}
 *   isLoading={false}
 *   onRegenerate={() => handleRegenerate()}
 * />
 */

import { useMemo } from 'react';
import { Loader2, RefreshCw, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

// ============================================
// TYPES
// ============================================

interface GeneratedImageDisplayProps {
  imageUrl?: string;
  aspectRatio: '1:1' | '4:5' | '9:16' | '16:9';
  width?: number;
  height?: number;
  fileSize?: number;
  isLoading?: boolean;
  error?: string;
  onRegenerate?: () => void;
  onDownload?: () => void;
  className?: string;
}

// ============================================
// HELPERS
// ============================================

/**
 * Format file size to human-readable string
 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/**
 * Get aspect ratio label
 */
function getAspectRatioLabel(ratio: '1:1' | '4:5' | '9:16' | '16:9'): string {
  switch (ratio) {
    case '1:1':
      return 'Square';
    case '4:5':
      return 'Portrait';
    case '9:16':
      return 'Vertical';
    case '16:9':
      return 'Landscape';
    default:
      return ratio;
  }
}

// ============================================
// COMPONENT
// ============================================

const GeneratedImageDisplay = ({
  imageUrl,
  aspectRatio,
  width,
  height,
  fileSize,
  isLoading = false,
  error,
  onRegenerate,
  onDownload,
  className = '',
}: GeneratedImageDisplayProps) => {
  // ============================================
  // COMPUTED VALUES
  // ============================================

  const aspectRatioLabel = useMemo(() => getAspectRatioLabel(aspectRatio), [aspectRatio]);

  const formattedFileSize = useMemo(() => (fileSize ? formatFileSize(fileSize) : null), [fileSize]);

  const dimensionsText = useMemo(
    () => (width && height ? `${width} × ${height}` : null),
    [width, height]
  );

  // ============================================
  // RENDER LOADING STATE
  // ============================================

  if (isLoading) {
    return (
      <div
        className={`relative border rounded-lg overflow-hidden bg-muted flex items-center justify-center aspect-square ${className}`}
      >
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Generating image...</p>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER ERROR STATE
  // ============================================

  if (error) {
    return (
      <div
        className={`relative border border-destructive rounded-lg overflow-hidden bg-destructive/10 flex items-center justify-center aspect-square ${className}`}
      >
        <div className="flex flex-col items-center gap-3 p-6 text-center">
          <p className="text-sm text-destructive font-medium">Failed to generate image</p>
          <p className="text-xs text-muted-foreground">{error}</p>
          {onRegenerate && (
            <Button variant="outline" size="sm" onClick={onRegenerate}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          )}
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER EMPTY STATE
  // ============================================

  if (!imageUrl) {
    return (
      <div
        className={`relative border border-dashed rounded-lg overflow-hidden bg-muted flex items-center justify-center aspect-square ${className}`}
      >
        <p className="text-sm text-muted-foreground">No image generated yet</p>
      </div>
    );
  }

  // ============================================
  // RENDER IMAGE
  // ============================================

  return (
    <div className={`relative ${className}`}>
      {/* Image container with aspect ratio */}
      <div
        className={`relative border rounded-lg overflow-hidden bg-muted ${
          aspectRatio === '1:1'
            ? 'aspect-square'
            : aspectRatio === '4:5'
              ? 'aspect-[4/5]'
              : aspectRatio === '9:16'
                ? 'aspect-[9/16]'
                : 'aspect-[16/9]'
        }`}
      >
        <img
          src={imageUrl}
          alt="Generated social media image"
          className="w-full h-full object-cover"
          loading="lazy"
        />

        {/* Overlay with actions on hover */}
        <div className="absolute inset-0 bg-black/60 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          {onRegenerate && (
            <Button variant="secondary" size="sm" onClick={onRegenerate}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Regenerate
            </Button>
          )}
          {onDownload && (
            <Button variant="secondary" size="sm" onClick={onDownload}>
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          )}
        </div>
      </div>

      {/* Metadata footer */}
      <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-xs">
            {aspectRatioLabel} ({aspectRatio})
          </Badge>
          {dimensionsText && <span>{dimensionsText}</span>}
        </div>
        {formattedFileSize && <span>{formattedFileSize}</span>}
      </div>
    </div>
  );
};

export { GeneratedImageDisplay };
